import { getAllSpecialConditions, SPECIAL_CONDITIONS } from '@/pages/panel-interno/planning-page/model/constants/special-conditions'
import { cn } from '@/shared/lib/utils'
import { Card } from '@/shared/ui/card'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shared/ui/tooltip'
import { HotelReservation, Pet } from '@monorepo/functions/src/types/reservations'

import { Room } from '../model/room'

interface RoomCardProps extends React.HTMLAttributes<HTMLDivElement> {
    room: Room
    onClick: () => void
    reservations: HotelReservation[]
}

export const RoomCard = ({ room, onClick, reservations, ...props }: RoomCardProps) => {
    const petsInRoom = reservations.flatMap(r => r.pets.filter(pet => pet.roomNumber === room.number).map((pet, index) => ({
        ...pet,
        conditions: getAllSpecialConditions(r, pet, index),
    })))
    const hasManyPets = petsInRoom.length > 2

    return (
        <Card
            className={cn(
                'flex h-full w-full cursor-pointer flex-col overflow-auto p-1 transition-colors hover:bg-gray-50',
                !petsInRoom.length && 'bg-gray-100',
            )}
            onClick={onClick}
            {...props}
        >
            <h2 className='mb-1 border-b text-base font-bold text-gray-700 xl:text-xl'>{room.number}</h2>
            <div className={cn('flex flex-row flex-wrap gap-1', petsInRoom.length === 0 && 'my-auto self-center')}>
                {petsInRoom.map((pet, index) => {
                    const petSymbols = pet.conditions.map(condition => SPECIAL_CONDITIONS.find(c => c.type === condition.type)?.symbol)
                    return (
                        <div
                            key={index}
                            className={cn('flex w-fit flex-row flex-wrap items-center gap-1 rounded-lg bg-blue-50 p-1')}
                        >
                            <p className={cn('whitespace-nowrap text-sm font-semibold xl:text-lg')}>{pet.name}</p>
                            <div className='flex h-auto flex-row flex-wrap items-center gap-1'>
                                {petSymbols.map((symbol, symbolIndex) => {
                                    const condition = SPECIAL_CONDITIONS.find(c => c.symbol === symbol)
                                    return condition ? (
                                        <TooltipProvider key={symbolIndex}>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <span
                                                        className={cn(
                                                            'inline-flex items-center justify-center font-semibold',
                                                            hasManyPets
                                                                ? 'h-3 w-3 text-xs xl:h-4 xl:w-4 xl:text-sm'
                                                                : 'h-4 w-4 text-base xl:h-5 xl:w-5 xl:text-lg',
                                                        )}
                                                    >
                                                        {symbol}
                                                    </span>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <p className={hasManyPets ? 'text-lg' : 'text-xl'}>
                                                        {condition.label}
                                                    </p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    ) : null
                                })}
                            </div>
                        </div>
                    )
                })}
                {petsInRoom.length === 0 && <span className='text-base text-gray-500'>Vacía</span>}
            </div>
        </Card>
    )
}
